import API_CONFIG from '../config/api';

export interface SupplementaryInsurance {
  _id: string;
  name: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSupplementaryInsuranceData {
  name: string;
  active?: boolean;
}

export interface UpdateSupplementaryInsuranceData {
  name?: string;
  active?: boolean;
}

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    headers: {
      'Content-Type': 'application/json',
      Authorization: token ? `Bearer ${token}` : '',
    },
  };
};

/**
 * Get active supplementary insurances (for public use)
 */
export const getActiveSupplementaryInsurances = async (): Promise<SupplementaryInsurance[]> => {
  try {
    const response = await fetch(API_CONFIG.getUrl('supplementary-insurances/active'), {
      method: 'GET',
      ...getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.message || 'خطا در دریافت بیمه های تکمیلی فعال');
    }
  } catch (error) {

    throw error;
  }
};

/**
 * Get active supplementary insurance by ID (for public use)
 */
export const getActiveSupplementaryInsuranceById = async (id: string): Promise<SupplementaryInsurance> => {
  try {
    const response = await fetch(API_CONFIG.getUrl(`supplementary-insurances/details/${id}`), {
      method: 'GET',
      ...getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.message || 'خطا در دریافت بیمه تکمیلی');
    }
  } catch (error) {

    throw error;
  }
};

export interface SupplementaryInsurancePaginationResponse {
  data: SupplementaryInsurance[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    limit: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

/**
 * Get all supplementary insurances (admin only) with pagination
 */
export const getAllSupplementaryInsurances = async (page: number = 1, limit: number = 6): Promise<SupplementaryInsurancePaginationResponse> => {
  try {
    const response = await fetch(API_CONFIG.getUrl(`supplementary-insurances?page=${page}&limit=${limit}`), {
      method: 'GET',
      ...getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    if (result.success) {
      return {
        data: result.data,
        pagination: result.pagination
      };
    } else {
      throw new Error(result.message || 'خطا در دریافت بیمه های تکمیلی');
    }
  } catch (error) {

    throw error;
  }
};

/**
 * Get supplementary insurance by ID (admin only)
 */
export const getSupplementaryInsuranceById = async (id: string): Promise<SupplementaryInsurance> => {
  try {
    const response = await fetch(API_CONFIG.getUrl(`supplementary-insurances/${id}`), {
      method: 'GET',
      ...getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.message || 'خطا در دریافت بیمه تکمیلی');
    }
  } catch (error) {

    throw error;
  }
};

/**
 * Create a new supplementary insurance (admin only)
 */
export const createSupplementaryInsurance = async (data: CreateSupplementaryInsuranceData): Promise<SupplementaryInsurance> => {
  try {
    const response = await fetch(API_CONFIG.getUrl('supplementary-insurances'), {
      method: 'POST',
      ...getAuthHeaders(),
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.message || 'خطا در ایجاد بیمه تکمیلی');
    }
  } catch (error) {

    throw error;
  }
};

/**
 * Update an existing supplementary insurance (admin only)
 */
export const updateSupplementaryInsurance = async (id: string, data: UpdateSupplementaryInsuranceData): Promise<SupplementaryInsurance> => {
  try {
    const response = await fetch(API_CONFIG.getUrl(`supplementary-insurances/${id}`), {
      method: 'PUT',
      ...getAuthHeaders(),
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.message || 'خطا در به‌روزرسانی بیمه تکمیلی');
    }
  } catch (error) {

    throw error;
  }
};

/**
 * Toggle supplementary insurance status (admin only)
 */
export const toggleSupplementaryInsuranceStatus = async (id: string): Promise<SupplementaryInsurance> => {
  try {
    const response = await fetch(API_CONFIG.getUrl(`supplementary-insurances/${id}/toggle-status`), {
      method: 'PATCH',
      ...getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.message || 'خطا در تغییر وضعیت بیمه تکمیلی');
    }
  } catch (error) {

    throw error;
  }
};

/**
 * Delete a supplementary insurance (admin only)
 */
export const deleteSupplementaryInsurance = async (id: string): Promise<void> => {
  try {
    const response = await fetch(API_CONFIG.getUrl(`supplementary-insurances/${id}`), {
      method: 'DELETE',
      ...getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    if (!result.success) {
      throw new Error(result.message || 'خطا در حذف بیمه تکمیلی');
    }
  } catch (error) {

    throw error;
  }
};

/**
 * Utility function to get supplementary insurance name by ID with caching
 * This can be used across components to resolve supplementary insurance names
 */
const supplementaryInsuranceCache = new Map<string, SupplementaryInsurance>();

export const getSupplementaryInsuranceNameById = async (insuranceId: string): Promise<string> => {
  if (!insuranceId) return '';
  
  // Check cache first
  if (supplementaryInsuranceCache.has(insuranceId)) {
    const cached = supplementaryInsuranceCache.get(insuranceId);
    return cached ? cached.name : 'حذف شده';
  }
  
  try {
    const insurance = await getActiveSupplementaryInsuranceById(insuranceId);
    supplementaryInsuranceCache.set(insuranceId, insurance);
    return insurance.name;
  } catch (error) {

    // If insurance not found or deleted, return "حذف شده"
    return 'حذف شده';
  }
};

/**
 * Clear supplementary insurance cache (useful when insurance data changes)
 */
export const clearSupplementaryInsuranceCache = () => {
  supplementaryInsuranceCache.clear();
};